import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CustomerSelectionModal } from '../CustomerSelectionModal';
import ProductSelectionModal from '../ProductSelectionModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useNotifications } from '../../store/useStore';
import type { Database } from '../../types/database.types';
import { createInvoice, calculateInvoiceTotals, type DocumentType } from '../../lib/invoiceUtils';
import { supabase } from '../../lib/supabase';
import { Package, Plus, Trash2 } from 'lucide-react';

type Customer = Database['public']['Tables']['customers']['Row'];
type Product = Database['public']['Tables']['products']['Row'];
type PaymentMethod = Database['public']['Tables']['payment_methods']['Row'];
type Carrier = Database['public']['Tables']['carriers']['Row'];
type PackageType = Database['public']['Tables']['package_types']['Row'];

interface InvoiceWithDeliveryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface InvoiceItem {
  id: string;
  product_id: string | null;
  product: Product | null;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percentage: number;
  vat_rate: number;
}

export const InvoiceWithDeliveryFormModal: React.FC<InvoiceWithDeliveryFormModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const { addNotification } = useNotifications();

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [status, setStatus] = useState<'draft' | 'issued'>('issued');
  const [saving, setSaving] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [pendingQuantityValues, setPendingQuantityValues] = useState<Record<string, string>>({});
  const [pendingPriceValues, setPendingPriceValues] = useState<Record<string, string>>({});
  const [pendingDiscountValues, setPendingDiscountValues] = useState<Record<string, string>>({});
  const [pendingVatValues, setPendingVatValues] = useState<Record<string, string>>({});
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  // Campi specifici per Fattura Accompagnatoria
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [packageTypes, setPackageTypes] = useState<PackageType[]>([]);
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
  const [selectedPackageTypeId, setSelectedPackageTypeId] = useState<string>('');
  const [packageCount, setPackageCount] = useState<string>('');
  const [weightKg, setWeightKg] = useState<string>('');
  const [destinationAddress, setDestinationAddress] = useState<string>('');
  const [differentDestination, setDifferentDestination] = useState<boolean>(false);
  const [transportDate, setTransportDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    const loadData = async () => {
      try {
        const [paymentMethodsResult, carriersResult, packageTypesResult] = await Promise.all([
          supabase
            .from('payment_methods')
            .select('*')
            .eq('is_active', true)
            .order('name'),
          supabase
            .from('carriers')
            .select('*')
            .eq('is_active', true)
            .order('name'),
          supabase
            .from('package_types')
            .select('*')
            .eq('is_active', true)
            .order('name'),
        ]);

        if (paymentMethodsResult.error) throw paymentMethodsResult.error;
        if (carriersResult.error) throw carriersResult.error;
        if (packageTypesResult.error) throw packageTypesResult.error;

        setPaymentMethods(paymentMethodsResult.data || []);
        setCarriers(carriersResult.data || []);
        setPackageTypes(packageTypesResult.data || []);
      } catch (error: any) {
        console.error('Errore caricamento dati:', error);
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Aggiorna indirizzo destinazione quando cambia cliente o checkbox
  useEffect(() => {
    if (selectedCustomer && !differentDestination) {
      // Usa l'indirizzo del cliente come default
      const customerAddress = [
        selectedCustomer.address,
        selectedCustomer.city,
        selectedCustomer.postal_code,
        selectedCustomer.province
      ].filter(Boolean).join(', ');
      setDestinationAddress(customerAddress);
    } else if (!differentDestination) {
      setDestinationAddress('');
    }
  }, [selectedCustomer, differentDestination]);

  const resetForm = () => {
    setSelectedCustomer(null);
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setItems([]);
    setStatus('issued');
    setPaymentTerms('');
    setNotes('');
    setPendingQuantityValues({});
    setPendingPriceValues({});
    setPendingDiscountValues({});
    setPendingVatValues({});
            // Reset campi Fattura Accompagnatoria
            setSelectedCarrierId('');
            setSelectedPackageTypeId('');
      setPackageCount('');
      setWeightKg('');
      setDestinationAddress('');
      setDifferentDestination(false);
            setTransportDate(new Date().toISOString().slice(0, 10));
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    if (!selectedCustomer) {
      addNotification({
        type: 'error',
        title: 'Cliente mancante',
        message: 'Seleziona un cliente per fattura accompagnatoria.',
      });
      return;
    }

    const validItems = items.filter(
      (item) => item.description.trim() && item.quantity > 0 && item.unit_price >= 0
    );

    if (validItems.length === 0) {
      addNotification({
        type: 'error',
        title: 'Prodotti mancanti',
        message: 'Aggiungi almeno un prodotto valido alfattura accompagnatoria.',
      });
      return;
    }

    setSaving(true);
    try {
      const invoiceItems = validItems.map((item) => ({
        product_id: item.product_id,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        discount_percentage: item.discount_percentage,
        vat_rate: item.vat_rate,
      }));

      if (!selectedCarrierId) {
        addNotification({
          type: 'error',
          title: 'Corriere mancante',
          message: 'Seleziona un corriere per la fattura accompagnatoria.',
        });
        setSaving(false);
        return;
      }

      await createInvoice({
        customer_id: selectedCustomer.id,
        order_id: null,
        invoice_date: invoiceDate,
        payment_terms: paymentTerms || selectedCustomer.payment_terms || null,
        notes: notes || null,
        status,
        document_type: 'invoice_with_delivery',
        items: invoiceItems,
        // Campi specifici Fattura Accompagnatoria (stessi campi del DDT)
        carrier_id: selectedCarrierId,
        destination_address: differentDestination ? destinationAddress || null : (selectedCustomer ? `${selectedCustomer.address || ''}, ${selectedCustomer.city || ''}${selectedCustomer.postal_code ? `, ${selectedCustomer.postal_code}` : ''}`.trim() : null),
        transport_date: transportDate || null,
        numero_colli: packageCount ? parseInt(packageCount, 10) : null,
        package_type_id: selectedPackageTypeId || null,
        peso: weightKg ? parseFloat(weightKg.replace(',', '.')) : null,
      });

      addNotification({
        type: 'success',
        title: 'Documento creato',
        message: 'Il documento è stato creato correttamente.',
      });

      resetForm();
      onCreated();
      onClose();
    } catch (error: any) {
      console.error('Errore salvataggio fattura accompagnatoria:', error);
      
      let errorMessage = 'Si è verificato un errore durante la creazione del documento.';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.details) {
        errorMessage = error.details;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      addNotification({
        type: 'error',
        title: 'Errore creazione documento',
        message: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleProductsSelected = (products: Product[]) => {
    const newItems: InvoiceItem[] = products.map(product => ({
      id: `item-${Date.now()}-${Math.random()}`,
      product_id: product.id,
      product: product,
      description: product.name + (product.description ? ` - ${product.description}` : ''),
      quantity: 1,
      unit: product.unit || 'pz',
      unit_price: product.base_price || 0,
      discount_percentage: 0,
      vat_rate: product.iva ?? 22,
    }));

    setItems(prev => [...prev, ...newItems]);
    setShowProductModal(false);
  };

  const handleDeleteItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const handleQuantityChange = (id: string, quantity: number) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, quantity } : item
    ));
  };

  const handlePriceChange = (id: string, price: number) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, unit_price: price } : item
    ));
  };

  const handleDiscountChange = (id: string, discount: number) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, discount_percentage: discount } : item
    ));
  };

  const handleVatChange = (id: string, vat: number) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, vat_rate: vat } : item
    ));
  };

  const handleDescriptionChange = (id: string, description: string) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, description } : item
    ));
  };

  const existingProductIds = useMemo(() => new Set(items.map(item => item.product_id).filter(Boolean) as string[]), [items]);

  const totals = useMemo(() => {
    const invoiceItems = items.map((item) => ({
      product_id: item.product_id,
      description: item.description || '',
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
      discount_percentage: item.discount_percentage,
      vat_rate: item.vat_rate,
    }));
    return calculateInvoiceTotals(invoiceItems);
  }, [items]);

  const renderProductCard = (item: InvoiceItem) => {
    return (
      <div key={item.id} className="bg-white border border-green-200 rounded p-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 sm:flex-wrap xl:flex-nowrap">
          <div className="flex items-center gap-2 w-full min-w-0 sm:min-w-[220px] sm:flex-1">
            {item.product?.photo_url && (
              <img
                src={item.product.photo_url}
                alt={item.product.name}
                className="w-8 h-8 object-cover rounded border"
              />
            )}
            <div className="flex-1">
              <Input
                value={item.description}
                onChange={(e) => handleDescriptionChange(item.id, e.target.value)}
                className="h-5 text-xs font-medium text-gray-900 mb-1"
                placeholder="Descrizione"
              />
              {item.product && (
                <p className="text-xs text-gray-500">{item.product.code}</p>
              )}
            </div>
          </div>

          {item.discount_percentage > 0 && (
            <div className="text-xs text-green-700 font-semibold min-w-[70px]">
              Sconto {item.discount_percentage}%
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 sm:contents">
          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[140px]">
            <span className="text-xs text-gray-600 w-full sm:w-auto">Quantità:</span>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*[.,]?[0-9]*"
              value={pendingQuantityValues[item.id] ?? item.quantity.toString()}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                  setPendingQuantityValues(prev => ({
                    ...prev,
                    [item.id]: value
                  }));
                }
              }}
              onBlur={() => {
                const rawValue = pendingQuantityValues[item.id] ?? item.quantity.toString();
                const parsedValue = parseFloat(rawValue.replace(',', '.'));
                const finalValue = !isNaN(parsedValue) && parsedValue > 0 ? parsedValue : item.quantity;
                if (finalValue !== item.quantity) {
                  handleQuantityChange(item.id, finalValue);
                }
                setPendingQuantityValues(prev => {
                  const { [item.id]: _removed, ...rest } = prev;
                  return rest;
                });
              }}
              className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-xs text-gray-500">{item.unit}</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[130px]">
            <span className="text-xs text-gray-600 w-full sm:w-auto">Prezzo:</span>
            <div className="relative flex-1 min-w-0 sm:flex-none">
              <Input
                type="text"
                inputMode="decimal"
                value={
                  pendingPriceValues[item.id] ??
                  (item.unit_price !== null && item.unit_price !== undefined
                    ? String(item.unit_price)
                    : '')
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                    setPendingPriceValues(prev => ({
                      ...prev,
                      [item.id]: value,
                    }));
                  }
                }}
                onBlur={() => {
                  const raw =
                    pendingPriceValues[item.id] ??
                    (item.unit_price !== null && item.unit_price !== undefined
                      ? String(item.unit_price)
                      : '');

                  if (raw === '') {
                    setPendingPriceValues(prev => {
                      const { [item.id]: _removed, ...rest } = prev;
                      return rest;
                    });
                    return;
                  }

                  const parsed = parseFloat(raw.replace(',', '.'));
                  if (isNaN(parsed)) {
                    setPendingPriceValues(prev => {
                      const { [item.id]: _removed, ...rest } = prev;
                      return rest;
                    });
                    return;
                  }

                  const normalized = Number(parsed.toFixed(2));
                  if (normalized !== Number(item.unit_price ?? 0)) {
                    handlePriceChange(item.id, normalized);
                  } else {
                    setPendingPriceValues(prev => {
                      const { [item.id]: _removed, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                className="h-5 text-xs w-full sm:w-24 pr-4 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-500">€</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[100px]">
            <span className="text-xs text-gray-600 w-full sm:w-auto">Sconto:</span>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*[.,]?[0-9]*"
              value={pendingDiscountValues[item.id] ?? item.discount_percentage.toString()}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                  setPendingDiscountValues(prev => ({
                    ...prev,
                    [item.id]: value
                  }));
                }
              }}
              onBlur={() => {
                const rawValue = pendingDiscountValues[item.id] ?? item.discount_percentage.toString();
                const parsedValue = parseFloat(rawValue.replace(',', '.'));
                const finalValue = !isNaN(parsedValue) && parsedValue >= 0 ? Math.min(parsedValue, 100) : item.discount_percentage;
                if (finalValue !== item.discount_percentage) {
                  handleDiscountChange(item.id, finalValue);
                }
                setPendingDiscountValues(prev => {
                  const { [item.id]: _removed, ...rest } = prev;
                  return rest;
                });
              }}
              className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-xs text-gray-500">%</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[100px]">
            <span className="text-xs text-gray-600 w-full sm:w-auto">IVA:</span>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*[.,]?[0-9]*"
              value={pendingVatValues[item.id] ?? item.vat_rate.toString()}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                  setPendingVatValues(prev => ({
                    ...prev,
                    [item.id]: value
                  }));
                }
              }}
              onBlur={() => {
                const rawValue = pendingVatValues[item.id] ?? item.vat_rate.toString();
                const parsedValue = parseFloat(rawValue.replace(',', '.'));
                const finalValue = !isNaN(parsedValue) && parsedValue >= 0 ? parsedValue : item.vat_rate;
                if (finalValue !== item.vat_rate) {
                  handleVatChange(item.id, finalValue);
                }
                setPendingVatValues(prev => {
                  const { [item.id]: _removed, ...rest } = prev;
                  return rest;
                });
              }}
              className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-xs text-gray-500">%</span>
          </div>

          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeleteItem(item.id)}
            className="h-8 sm:h-5 w-full sm:w-auto text-xs px-1 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3" />
            <span className="ml-1 sm:hidden">Rimuovi riga</span>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="flex flex-col gap-3 overflow-hidden sm:max-h-[90vh] sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Nuova Fattura Accompagnatoria</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-2">
            {/* Cliente */}
            <div className="space-y-1">
              <Label>Cliente *</Label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  readOnly
                  value={selectedCustomer?.company_name || 'Nessun cliente selezionato'}
                  className="bg-gray-50"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCustomerModal(true)}
                >
                  Seleziona
                </Button>
              </div>
            </div>

            {/* Dati fattura */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Data fattura *</Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>Stato *</Label>
                <select
                  className="w-full h-10 px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'draft' | 'issued')}
                >
                  <option value="draft">Bozza</option>
                  <option value="issued">Emessa</option>
                </select>
              </div>
            </div>

            {/* Dati trasporto Fattura Accompagnatoria */}
            <div className="space-y-4 border rounded-md p-4 bg-green-50 border-green-200">
              <h3 className="text-lg font-semibold text-green-800">Dati Trasporto</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Corriere *</Label>
                  <Select
                    value={selectedCarrierId}
                    onValueChange={setSelectedCarrierId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona corriere" />
                    </SelectTrigger>
                    <SelectContent>
                      {carriers.map((carrier) => (
                        <SelectItem key={carrier.id} value={carrier.id}>
                          {carrier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Data trasporto *</Label>
                  <Input
                    type="date"
                    value={transportDate}
                    onChange={(e) => setTransportDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Numero colli</Label>
                  <Input
                    type="number"
                    min="1"
                    value={packageCount}
                    onChange={(e) => setPackageCount(e.target.value)}
                    placeholder="Numero di colli"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Tipo di collo</Label>
                  <Select
                    value={selectedPackageTypeId}
                    onValueChange={setSelectedPackageTypeId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona tipo di collo" />
                    </SelectTrigger>
                    <SelectContent>
                      {packageTypes.map((pkgType) => (
                        <SelectItem key={pkgType.id} value={pkgType.id}>
                          {pkgType.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Peso (kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    placeholder="Peso in chilogrammi"
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <div className="flex items-center space-x-2 mb-2">
                    <input
                      type="checkbox"
                      id="differentDestinationFA"
                      checked={differentDestination}
                      onChange={(e) => setDifferentDestination(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <Label htmlFor="differentDestinationFA" className="cursor-pointer">
                      Destinazione diversa
                    </Label>
                  </div>
                  {differentDestination && (
                    <>
                      <Label>Indirizzo di destinazione</Label>
                      <Input
                        value={destinationAddress}
                        onChange={(e) => setDestinationAddress(e.target.value)}
                        placeholder="Indirizzo di destinazione della merce"
                      />
                    </>
                  )}
                  {!differentDestination && selectedCustomer && (
                    <p className="text-xs text-gray-500 mt-1">
                      Usando indirizzo cliente: {[selectedCustomer.address, selectedCustomer.city, selectedCustomer.postal_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modalità di pagamento e note */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Modalità di pagamento</Label>
                <Select
                  value={paymentTerms || ''}
                  onValueChange={setPaymentTerms}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={selectedCustomer?.payment_terms || "Seleziona modalità di pagamento"} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCustomer?.payment_terms && !paymentMethods.some(pm => pm.name === selectedCustomer.payment_terms) && (
                      <SelectItem value={selectedCustomer.payment_terms}>
                        {selectedCustomer.payment_terms} (da cliente)
                      </SelectItem>
                    )}
                    {paymentMethods.map((method) => (
                      <SelectItem key={method.id} value={method.name}>
                        {method.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!paymentTerms && selectedCustomer?.payment_terms && (
                  <p className="text-xs text-gray-500 mt-1">
                    Default cliente: {selectedCustomer.payment_terms}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Note</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Note aggiuntive (opzionale)"
                />
              </div>
            </div>

            {/* Prodotti */}
            <div className="space-y-3 border rounded-md p-3 bg-green-50 border-green-200">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <div className="flex items-center space-x-2">
                  <Package className="w-5 h-5 text-green-600" />
                  <Label className="mb-0 text-lg font-semibold text-green-800">
                    Prodotti ({items.length})
                  </Label>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setShowProductModal(true)}
                  className="h-9 sm:h-7 text-xs px-3 bg-primary-600 hover:bg-primary-700"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Aggiungi Prodotti
                </Button>
              </div>

              {items.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <p className="text-sm text-green-700">
                    Aggiungi prodotti alla fattura
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {items.map(item => renderProductCard(item))}
                </div>
              )}

              {/* Totali */}
              {items.length > 0 && (
                <div className="pt-2 border-t border-green-300 text-xs text-gray-700 flex flex-wrap justify-end gap-x-4 gap-y-1">
                  <div>Imponibile: € {totals.taxable_amount.toFixed(2)}</div>
                  <div>IVA: € {totals.tax_amount.toFixed(2)}</div>
                  <div className="font-semibold">
                    Totale: € {totals.total_amount.toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva fattura accompagnatoria'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selezione cliente */}
      <CustomerSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerSelect={(c) => {
          setSelectedCustomer(c);
          // Imposta il payment_terms del cliente come default se disponibile
          if (c.payment_terms) {
            setPaymentTerms(c.payment_terms);
          } else {
            setPaymentTerms('');
          }
        }}
        selectedCustomerId={selectedCustomer?.id}
        title="Seleziona cliente per la fattura accompagnatoria"
        description="Scegli il cliente a cui intestare la fattura accompagnatoria"
      />
      
      {/* Selezione prodotti */}
      <ProductSelectionModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        mode="select"
        onProductsSelected={handleProductsSelected}
        currentCustomer={selectedCustomer}
        existingProductIds={existingProductIds}
      />
    </>
  );
};
